import { defineMessages } from 'react-intl'
import { v4 } from 'uuid'

import type { SitterStatus } from '@app/constants/ApiTypes/sitterStatus'

import { assertNever } from '@app/utils/assertNever'
import { createSelector } from '@app/utils/createSelector'
import moment from '@app/utils/moment'
import { normalize } from '@app/utils/normalizer'
import { catchFetchDataError } from '@app/utils/performFetchData'

import { getSitterOnboarding } from '@app/packages/api/v2/actions/sitter_onboarding'
import type { ApiOnboarding, ApiOnboardingStep, ApiOnboardingStepTesting, ApiOnboardingStepTraining } from '@app/packages/api/v2/types/entities'
import type { ApiDataResponse } from '@app/packages/api/v2/types/misc'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { callV2Api } from '@app/store/actions/api'
import {
  approvedBannerSeenStateKeyValue,
  onboardingApplicationStepsSeenStateKeyValue,
  onboardingPostTrainingTestPassedSeenKeyValue,
  onboardingTestPassedSeenStateKeyValue,
  onboardingTrainingPassedSeenKeyValue,
} from '@app/store/actions/sitter_onboarding'
import { createReduxSlice } from '@app/store/redux_slice'
import { contactsListResultSelector } from '@app/store/slices/contacts'
import { onboardingV2EnabledResultSelector } from '@app/store/slices/features'
import { intlSelector } from '@app/store/slices/intl'

import { profileUserResultSelector } from './profile'

const onboardingSyncSlice = createReduxSlice<{ hash: string }>('sitter_onboarding_v2_sync')
const onboardingSlice = createReduxSlice<SliceState<ApiDataResponse<ApiOnboarding>>>('sitter_onboarding')

export const invalidateOnboarding = () => onboardingSyncSlice.set({ hash: v4() })

export const onboardingVersionResultSelector = createSelector([onboardingV2EnabledResultSelector], v2Result =>
  createLoadable(c => {
    const v2Enabled = c.unwrap(v2Result)
    if (v2Enabled) return 2
    return 1
  })
)

const onboardingResponseResultSelector = createSelector(
  [onboardingSlice.selector, onboardingSyncSlice.selector, profileUserResultSelector, onboardingVersionResultSelector],
  (sliceState, sync, userResult, onboardingVersionResult) =>
    createLoadable(c => {
      const user = c.unwrap(userResult)
      if (!user) throw new Error('User is missing')
      if (user.account_type !== 'sitter') throw new Error('User is not sitter')
      const enabled = c.unwrap(onboardingVersionResult) >= 2
      if (!enabled) throw new Error('Onboarding v2 is not enabled')
      return withCheckedSliceState(c, {
        sliceState,
        slice: onboardingSlice,
        hash: [user.id, user.has_training_request, user.training_completed, sync?.hash ?? ''].join(':'),
        load: async dispatch => await dispatch(callV2Api(getSitterOnboarding())).catch(catchFetchDataError(dispatch, 'Onboarding load failed')),
      })
    })
)

const onboardingResultSelector = mapThunkSelector(onboardingResponseResultSelector, response => response.data)

export const onboardingStepsSelector = createSelector([onboardingSlice.selector], slice => (slice ? (normalize(slice.data).onboarding_steps ?? {}) : {}))

const onboardingSchoolsSelector = createSelector([onboardingSlice.selector], slice => (slice ? (normalize(slice.data).schools ?? {}) : {}))

export const sitterOnboardingStatusSelector = createSelector(
  [
    onboardingVersionResultSelector,
    profileUserResultSelector,
    onboardingResultSelector,
    onboardingStepsSelector,
    onboardingApplicationStepsSeenStateKeyValue.selector,
    onboardingTestPassedSeenStateKeyValue.selector,
    onboardingPostTrainingTestPassedSeenKeyValue.selector,
    onboardingTrainingPassedSeenKeyValue.selector,
    approvedBannerSeenStateKeyValue.selector,
    onboardingSchoolsSelector,
    contactsListResultSelector,
    intlSelector,
  ],
  (
    onboardingVersionResult,
    userResult,
    onboardingResult,
    onboardingSteps,
    stepsSeenResult,
    testPassedSeenResult,
    postTrainingTestPassedSeenResult,
    trainingPassedSeenResult,
    approvedBannerSeenResult,
    schools,
    contactsResult,
    intl
  ) =>
    createLoadable<SitterStatus>(c => {
      const { formatMessage } = intl
      const user = c.unwrap(userResult)
      if (user?.account_type !== 'sitter') throw new Error('User is not sitter')

      const shouldApprovedBannerBeVisible = () => {
        if (!user.approved) throw new Error('User is not yet approved')
        if (!user.approved_at) throw new Error('User has missing approve date')
        if (moment(user.approved_at).isBefore('2025-02-20T14:00:00+03:00')) return false

        return !c.unwrap(approvedBannerSeenResult).value?.seen
      }

      if (user.approved) {
        return { type: 'approved', banner_visible: shouldApprovedBannerBeVisible() }
      }

      const enabled = c.unwrap(onboardingVersionResult) >= 2
      if (!enabled) return { type: 'v2_disabled' }
      if (typeof user.age === 'number' && user.age < 18) return { type: 'application_on_hold', reason: 'underage', min_age: 18 }

      const onboarding = c.unwrap(onboardingResult)
      if (!onboarding.relationships.steps.data) throw new Error('No onboarding relationships')
      const steps = onboarding.relationships.steps.data.map(d => {
        const step = onboardingSteps[d.id]
        if (!step) throw new Error(`Step ${d.id} not found`)
        return step
      })

      const stepsSeen = c.unwrap(stepsSeenResult)
      if (!stepsSeen.value?.seen) return { type: 'application_accepted' }

      const contact = (() => {
        const contacts = c.unwrap(contactsResult)
        const region_id = String(user.region_id)
        const contact =
          contacts.find(c => c.attributes.department === 'school' && c.region && c.region.id === region_id) ||
          contacts.find(c => c.attributes.department === 'school' && !c.region)
        if (!contact) return null
        const result = {
          whatsapp: contact.attributes.whatsapp ?? undefined,
          telegram: contact.attributes.telegram ?? undefined,
        }
        return result
      })()

      const hasRejectedStep = steps.find(s => {
        if (s.attributes.name === 'testing' && s.attributes.status === 'failed') {
          const testing = s as ApiOnboardingStepTesting
          const max = testing.attributes.max_attempts_count
          return max !== undefined && testing.attributes.attempts_count >= max
        }
        if (s.attributes.name === 'training' && s.attributes.status === 'rejected') return true
        if (s.attributes.name === 'profile' && s.attributes.status === 'disapproved') return true
        return false
      })

      if (hasRejectedStep) {
        const trainingStep = steps.find((s): s is ApiOnboardingStepTraining => s.attributes.name === 'training')
        if (!trainingStep) throw new Error('Training step is missing')
        const feedbackLink = trainingStep.attributes.feedback_link
        return {
          type: 'application_rejected',
          feedback_link: feedbackLink,
          refund: hasRejectedStep.attributes.name === 'training' ? { refund_duration_days: 10 } : undefined,
        }
      }

      for (const [step, nextSteps] of lookup(steps)) {
        if (step.attributes.bypass) continue
        const name = step.attributes.name
        switch (name) {
          case 'testing': {
            const status = step.attributes.status
            // Onboarding carries two steps named `testing` — the one before the training and the
            // one after it — and they are separate screens with a seen-marker each.
            const postTraining = step.attributes.test_type === 'post_training_test'
            switch (status) {
              case 'pending':
              case 'failed': {
                const testing = {
                  test_link: step.attributes.test_link,
                  test_link_data: step.attributes.test_link_data,
                  attempts_count: step.attributes.attempts_count,
                  max_attempts_count: step.attributes.max_attempts_count,
                  contacts: {
                    telegram: contact?.telegram,
                    whatsapp: contact?.whatsapp,
                  },
                }
                return postTraining ? { type: 'post_training_testing_required', ...testing } : { type: 'pre_training_testing_required', ...testing }
              }
              case 'passed': {
                const nextStep = nextSteps.at(0)
                if (nextStep?.attributes.name === 'chat') continue
                const testPassedSeen = c.unwrap(postTraining ? postTrainingTestPassedSeenResult : testPassedSeenResult)
                /** This screen carries the training booking instructions, so hold here until a training is booked */
                const nextStepAwaitsBooking = nextStep?.attributes.name === 'training' && nextStep.attributes.status === 'not_selected'
                if (testPassedSeen.value?.seen && !nextStepAwaitsBooking) continue
                if (postTraining) return { type: 'post_training_testing_passed' }
                return {
                  type: 'pre_training_testing_passed',
                  telegram_link: applyText(contact?.telegram, formatMessage(messages.want_to_know_test_results)),
                  whatsapp_link: applyText(contact?.whatsapp, formatMessage(messages.want_to_know_test_results)),
                }
              }
              default:
                return assertNever(status, () => new Error(`Unexpected testing status ${status}`))
            }
          }
          case 'chat': {
            if (step.attributes.bypass) continue
            if (step.attributes.completed) continue
            return { type: 'chat' }
          }
          case 'training': {
            const status = step.attributes.status
            switch (status) {
              case 'not_selected': {
                return {
                  type: 'training_required',
                  programs: [{ id: 'offline', link: '/school_program.pdf', name: formatMessage(messages.offline) }],
                  telegram_link: applyText(contact?.telegram, formatMessage(messages.want_to_choose_school_dates)),
                  whatsapp_link: applyText(contact?.whatsapp, formatMessage(messages.want_to_choose_school_dates)),
                }
              }
              case 'selected': {
                const schoolId = (step as ApiOnboardingStepTraining).relationships.school.data?.id
                if (!schoolId) throw new Error('No school id')
                const school = schools[schoolId]
                if (!school) throw new Error(`School ${schoolId} not found`)
                const online = school.attributes.format.includes('online')
                return {
                  type: 'training_pending',
                  training: online
                    ? {
                        type: 'online',
                        format: school.attributes.format,
                        start: school.attributes.start_time.datetime,
                        end: school.attributes.end_time.datetime,
                        start_timezone: school.attributes.start_time.timezone,
                        end_timezone: school.attributes.end_time.timezone,
                      }
                    : {
                        type: 'offline',
                        format: school.attributes.format,
                        start: school.attributes.start_time.datetime,
                        end: school.attributes.end_time.datetime,
                        start_timezone: school.attributes.start_time.timezone,
                        end_timezone: school.attributes.end_time.timezone,
                        address: school.attributes.address,
                      },
                  telegram_link: applyText(contact?.telegram, formatMessage(messages.question_regarding_the_school)),
                  whatsapp_link: applyText(contact?.whatsapp, formatMessage(messages.question_regarding_the_school)),
                }
              }
              case 'rejected':
                throw new Error('Unexpected training status rejected')
              case 'finished': {
                // The screen says only the profile is left, which a post-training test makes untrue; its screens take over.
                if (nextSteps.some(isPostTrainingTest)) continue
                const trainingPassedSeen = c.unwrap(trainingPassedSeenResult)
                if (!trainingPassedSeen.value?.seen) return { type: 'training_passed' }
                continue
              }
              default:
                return assertNever(status, () => new Error(`Unexpected training status ${status}`))
            }
          }
          case 'profile': {
            const status = step.attributes.status
            switch (status) {
              case 'pending': {
                const profileIncomplete = Object.entries(step.attributes.completeness)
                  .filter(([key, _value]) => key !== 'schedule')
                  .some(([_key, value]) => !value)
                return {
                  type: 'profile_complete_required',
                  schedule_completed: step.attributes.completeness.schedule,
                  profile_completed: !profileIncomplete,
                }
              }
              case 'profile_completed':
                return { type: 'profile_approval_required' }
              case 'in_review':
                return { type: 'profile_in_review' }
              case 'approved': {
                continue
              }
              case 'disapproved':
                return { type: 'profile_rejected' }
              default:
                return assertNever(status, () => new Error(`Unexpected profile status ${status}`))
            }
          }
          default:
            return assertNever(name, () => new Error(`Unexpected onboarding step ${name}`))
        }
      }

      return { type: 'approved', banner_visible: shouldApprovedBannerBeVisible() }
    })
)

export type CandidateSitterRouting = {
  main: 'registration' | 'profile'
  profile_available: boolean
  schedule_available: boolean
  messages_available: boolean
}

export const sitterCandidateRoutingResultSelector = createSelector([profileUserResultSelector, sitterOnboardingStatusSelector], (userResult, v2statusResult) =>
  createLoadable<CandidateSitterRouting | null>(c => {
    const user = c.unwrap(userResult)
    if (!user) return null

    if (user.account_type !== 'sitter') return null
    if (user.approved) return null

    const v2status = c.unwrap(v2statusResult)
    if (v2status.type === 'v2_disabled') {
      if (!user.training_completed) {
        return {
          main: 'registration' as const,
          schedule_available: false,
          profile_available: false,
          messages_available: false,
        }
      }

      return {
        main: 'profile',
        schedule_available: true,
        profile_available: true,
        messages_available: true,
      }
    }

    if (v2status.type === 'approved') return null

    const settingsAvailable = (() => {
      // `post_training_testing_passed` is in here because the profile is all that remains by then,
      // and that screen links straight to it.
      switch (v2status.type) {
        case 'profile_complete_required':
        case 'profile_approval_required':
        case 'profile_in_review':
        case 'profile_rejected':
        case 'post_training_testing_passed':
          return true
        case 'application_accepted':
        case 'application_rejected':
        case 'application_on_hold':
        case 'pre_training_testing_required':
        case 'pre_training_testing_passed':
        case 'post_training_testing_required':
        case 'training_required':
        case 'training_pending':
        case 'training_passed':
        case 'chat':
        default:
          return false
      }
    })()

    const mainMode = (() => {
      switch (v2status.type) {
        case 'profile_complete_required':
        case 'profile_approval_required':
        case 'profile_in_review':
        case 'profile_rejected':
          return 'profile' as const
        case 'application_accepted':
        case 'application_rejected':
        case 'application_on_hold':
        case 'pre_training_testing_required':
        case 'pre_training_testing_passed':
        case 'post_training_testing_required':
        case 'post_training_testing_passed':
        case 'training_required':
        case 'training_pending':
        case 'training_passed':
        case 'chat':
        default:
          return 'registration' as const
      }
    })()

    return {
      main: mainMode,
      schedule_available: settingsAvailable,
      profile_available: settingsAvailable,
      messages_available: settingsAvailable,
    }
  })
)

const applyText = (url: string | undefined, text: string) => {
  if (!url) return undefined
  return `${url}?text=${encodeURIComponent(text)}`
}

const messages = defineMessages({
  offline: { id: "store.selectors.sitter_onboarding.messages.offline.85c5", defaultMessage: 'Офлайн' },
  want_to_know_test_results: { id: "store.selectors.sitter_onboarding.messages.want_to_know_test_results.5871", defaultMessage: 'Хочу узнать результаты теста' },
  want_to_choose_school_dates: { id: "store.selectors.sitter_onboarding.messages.want_to_choose_school_dates.e435", defaultMessage: 'Хочу выбрать даты школы' },
  question_regarding_the_school: { id: "store.selectors.sitter_onboarding.messages.question_regarding_the_school.ed4d", defaultMessage: 'У меня есть вопрос по школе' },
})

const isPostTrainingTest = (step: ApiOnboardingStep) =>
  step.attributes.name === 'testing' && step.attributes.test_type === 'post_training_test' && !step.attributes.bypass

function* lookup<T>(arr: T[]) {
  for (const [index, item] of arr.entries()) {
    yield [item, arr.slice(index + 1)] as const
  }
}
